// Session storage + wiring the API client to it.
//
// The token lives in localStorage under the same keys the old portal used,
// so signed-in operators stay signed in across the swap. Importing this
// module configures the API client (token getter + 401 handler) before any
// component can make a request.

import { configureClient, resetSessionEnded } from '@/api/client'
import type { Me } from '@/api/types'

export const TOKEN_KEY = 'scorelm_admin_token'
export const USER_KEY = 'scorelm_admin_user'

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function storageSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* private mode: session lasts for this tab only */
  }
}
function storageRemove(key: string) {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** JWT `exp` in epoch ms, or null if unreadable. */
export function tokenExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.')
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof json.exp === 'number' ? json.exp * 1000 : null
  } catch {
    return null
  }
}

export function isTokenExpired(token: string, now: number = Date.now()): boolean {
  const exp = tokenExpiry(token)
  return exp === null || exp <= now
}

export function readToken(): string | null {
  return storageGet(TOKEN_KEY)
}

/** A stored session that's unexpired and belongs to a super-admin, else null. */
export function readSession(): { token: string; user: Me } | null {
  const token = storageGet(TOKEN_KEY)
  const raw = storageGet(USER_KEY)
  if (!token || !raw || isTokenExpired(token)) return null
  try {
    const user = JSON.parse(raw) as Me
    if (user?.platform_role !== 'super_admin') return null
    return { token, user }
  } catch {
    return null
  }
}

export function writeSession(token: string, user: Me) {
  resetSessionEnded()
  storageSet(TOKEN_KEY, token)
  storageSet(USER_KEY, JSON.stringify(user))
}

export function clearSession() {
  storageRemove(TOKEN_KEY)
  storageRemove(USER_KEY)
}

// ─── 401 → "session expired" event ──────────────────────────────────────────

type Listener = () => void
const expiredListeners = new Set<Listener>()

export function onSessionExpired(fn: Listener): () => void {
  expiredListeners.add(fn)
  return () => {
    expiredListeners.delete(fn)
  }
}

export function emitSessionExpired() {
  expiredListeners.forEach((fn) => fn())
}

configureClient({
  getToken: readToken,
  onUnauthorized: () => emitSessionExpired(),
})

// ─── Post-login redirect ────────────────────────────────────────────────────

export const DEFAULT_ROUTE = '/dashboard'

/**
 * Only same-origin app paths are allowed as a post-login target: no
 * protocol-relative "//evil.com", no backslash anywhere (browsers read "/\\"
 * as "//"), no control character (the URL parser strips tab and newline, so
 * "/\t/evil" would become "//evil"), and never a signed-out page (/login,
 * /invite/*, /reset-password, /forgot*), checked on the normalised, decoded path so
 * "/x/../login", "/LOGIN" and "/%6Cogin" are caught too. `next` is advisory: it
 * is always re-validated.
 */
export function safeNext(next: string | null | undefined): string {
  if (!next) return DEFAULT_ROUTE
  if (!next.startsWith('/') || next.startsWith('//')) return DEFAULT_ROUTE
  if (next.includes('\\')) return DEFAULT_ROUTE
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(next)) return DEFAULT_ROUTE
  let u: URL
  try {
    u = new URL(next, window.location.origin)
  } catch {
    return DEFAULT_ROUTE
  }
  if (u.origin !== window.location.origin) return DEFAULT_ROUTE
  if (u.pathname.startsWith('//')) return DEFAULT_ROUTE
  // React Router decodes the path before matching ('/%6Cogin' routes to /login),
  // so the signed-out pages are tested on the decoded path.
  let path: string
  try {
    path = decodeURIComponent(u.pathname).replace(/\/{2,}/g, '/').toLowerCase()
  } catch {
    return DEFAULT_ROUTE
  }
  if (/^\/(login|invite|reset-password)(\/|$)/.test(path) || /^\/forgot/.test(path)) return DEFAULT_ROUTE
  return u.pathname + u.search + u.hash
}
